import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Search, 
  BarChart3, 
  Users, 
  Zap, 
  MapPin,
  CheckCircle,
  ArrowRight,
  Star,
  Shield,
  Clock,
  TrendingUp,
  Database,
  Target,
  Trophy,
  Flame,
  Calculator,
  Sparkles,
  ChevronRight,
  Play,
  Layers,
  MousePointerClick,
  Rocket,
  PieChart,
  BadgeEuro,
  Building,
  LineChart,
  Award,
  Briefcase,
  Settings,
  Mail,
  Send,
  Phone,
  MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LandingPage = () => {
  const painPoints = [
    {
      problem: "Vous passez des heures à qualifier vos prospects ?",
      solution: "Notre matching intelligent RNIC + DPE qualifie automatiquement chaque copropriété"
    },
    {
      problem: "Difficile d'estimer le potentiel CEE rapidement ?",
      solution: "Calcul BAR-TH-179 instantané avec zones climatiques et valorisation financière"
    },
    {
      problem: "Vos commerciaux manquent de motivation ?",
      solution: "Gamification intégrée : points, badges, classements pour booster l'engagement"
    }
  ];

  const features = [
    {
      icon: Database,
      title: "Prospection Intelligente",
      description: "Accédez à +200K copropriétés qualifiées. Matching automatique RNIC + DPE ADEME pour cibler les bâtiments à fort potentiel énergétique.",
      highlight: "200K+ copros"
    },
    {
      icon: Calculator,
      title: "Estimation CEE Instantanée",
      description: "Calculez le potentiel BAR-TH-179 en un clic. Zone climatique, nombre de lots, valorisation financière automatique.",
      highlight: "BAR-TH-179"
    },
    {
      icon: Mail,
      title: "Automatisation Emails",
      description: "Créez des templates personnalisés avec variables dynamiques. Envoyez des emails professionnels en un clic, directement depuis la fiche prospect.",
      highlight: "Gain de temps"
    },
    {
      icon: Trophy,
      title: "Gamification Commerciale",
      description: "Transformez la prospection en challenge. Points par action, badges de performance, classement équipe en temps réel.",
      highlight: "Boost x3"
    },
    {
      icon: LineChart,
      title: "Pipeline & Reporting",
      description: "Visualisez votre pipeline commercial sur carte ou en Kanban. Suivez chaque opportunité du premier contact à la signature.",
      highlight: "Temps réel"
    },
    {
      icon: MapPin,
      title: "Cartographie Interactive",
      description: "Visualisez vos prospects sur une carte satellite. Attribuez des secteurs géographiques exclusifs à vos agents.",
      highlight: "Vue satellite"
    }
  ];

  const results = [
    { value: "x3", label: "Prospects qualifiés", sublabel: "par commercial / mois" },
    { value: "-70%", label: "Temps de qualification", sublabel: "automatisation matching" },
    { value: "+45%", label: "Taux de conversion", sublabel: "grâce au ciblage précis" },
    { value: "2 min", label: "Pour estimer un projet", sublabel: "contre 30 min avant" }
  ];

  const testimonials = [
    {
      name: "Laurent Mercier",
      role: "Directeur Commercial",
      company: "Rénovation Habitat+",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
      quote: "On a triplé notre volume de devis qualifiés en 3 mois. Le matching automatique nous fait gagner un temps fou sur la qualification."
    },
    {
      name: "Sophie Durand",
      role: "Gérante",
      company: "ÉcoRénov Solutions",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop",
      quote: "La gamification a transformé l'ambiance commerciale. Mes agents se challengent et les résultats suivent naturellement."
    },
    {
      name: "Marc Lefebvre",
      role: "Responsable Développement",
      company: "Copro Énergie France",
      image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop",
      quote: "Pouvoir présenter une estimation CEE précise dès le premier RDV change tout. Les syndics nous font confiance immédiatement."
    }
  ];

  const useCases = [
    {
      icon: Building,
      title: "Entreprises de Rénovation",
      description: "Identifiez les copropriétés éligibles et priorisez vos actions commerciales"
    },
    {
      icon: Briefcase,
      title: "Bureaux d'Études",
      description: "Qualifiez rapidement le potentiel CEE de chaque projet résidentiel"
    },
    {
      icon: Users,
      title: "Équipes Commerciales",
      description: "Boostez la performance avec des outils de prospection modernes"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/95 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-[#0F172A]">CoproEnergy</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#solutions" className="text-[#64748B] hover:text-[#1E3A8A] transition-colors text-sm font-medium">Solutions</a>
              <a href="#resultats" className="text-[#64748B] hover:text-[#1E3A8A] transition-colors text-sm font-medium">Résultats</a>
              <a href="#tarifs" className="text-[#64748B] hover:text-[#1E3A8A] transition-colors text-sm font-medium">Tarifs</a>
              <a href="#contact" className="text-[#64748B] hover:text-[#1E3A8A] transition-colors text-sm font-medium">Contact</a>
            </div>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#0F172A] font-medium" data-testid="nav-login-btn">
                  Connexion
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white rounded-full px-6 shadow-lg shadow-blue-900/20" data-testid="nav-register-btn">
                  Démonstration gratuite
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-12 lg:pt-36 lg:pb-20 px-6 lg:px-12 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Copy */}
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-6">
                <Rocket className="w-4 h-4 text-[#1E3A8A]" />
                <span className="text-sm font-semibold text-[#1E3A8A]">Gestion Commerciale Nouvelle Génération</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-[3.2rem] font-black text-[#0F172A] tracking-tight leading-[1.15] mb-6">
                Développez votre business 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6]"> rénovation énergétique</span>
                <span className="block text-2xl sm:text-3xl lg:text-4xl mt-2 font-bold text-[#475569]">en résidentiel collectif</span>
              </h1>
              
              <p className="text-xl text-[#475569] leading-relaxed mb-4">
                <strong className="text-[#0F172A]">Accédez à la base nationale des copropriétés et DPE.</strong>
              </p>
              <p className="text-lg text-[#64748B] leading-relaxed mb-8">
                CoproEnergy est la plateforme de gestion commerciale conçue pour les professionnels 
                ciblant le <strong className="text-[#0F172A]">résidentiel collectif</strong>. Identifiez, qualifiez et convertissez 
                plus de copropriétés grâce au matching automatique RNIC × DPE.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white rounded-full px-8 h-14 text-lg shadow-xl shadow-blue-900/20"
                    data-testid="hero-cta-btn"
                  >
                    Démarrer gratuitement
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="rounded-full px-8 h-14 text-lg border-2 border-slate-200 hover:border-slate-300 group"
                    data-testid="hero-login-btn"
                  >
                    <Play className="w-5 h-5 mr-2 text-[#1E3A8A]" />
                    Voir la démo
                  </Button>
                </Link>
              </div>
              
              {/* Quick value props */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-100">
                <div className="text-center">
                  <p className="text-2xl font-black text-[#1E3A8A]">7 jours</p>
                  <p className="text-xs text-[#64748B] mt-1">Essai gratuit</p>
                </div>
                <div className="text-center border-x border-slate-100">
                  <p className="text-2xl font-black text-[#1E3A8A]">200K+</p>
                  <p className="text-xs text-[#64748B] mt-1">Copropriétés</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-black text-[#1E3A8A]">x3</p>
                  <p className="text-xs text-[#64748B] mt-1">Productivité</p>
                </div>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative">
              {/* Main visual container */}
              <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 shadow-2xl">
                {/* Browser header */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 bg-slate-700 rounded-lg px-4 py-1.5 text-center">
                    <span className="text-xs text-slate-400">app.coproenergy.fr</span>
                  </div>
                </div>
                
                {/* Dashboard preview */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-slate-500">Pipeline</p>
                      <p className="text-lg font-bold text-slate-900">2.4 M€</p>
                      <p className="text-xs text-emerald-600">+24%</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-slate-500">Devis envoyés</p>
                      <p className="text-lg font-bold text-slate-900">47</p>
                      <p className="text-xs text-emerald-600">+12 ce mois</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-slate-500">Taux conv.</p>
                      <p className="text-lg font-bold text-slate-900">34%</p>
                      <p className="text-xs text-emerald-600">+8 pts</p>
                    </div>
                  </div>
                  
                  {/* Pipeline visual */}
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-slate-700">Pipeline Commercial</p>
                      <p className="text-xs text-slate-500">Cette semaine</p>
                    </div>
                    <div className="flex gap-1 h-8">
                      <div className="bg-blue-500 rounded-l-lg flex-[3] flex items-center justify-center">
                        <span className="text-[10px] font-bold text-white">12</span>
                      </div>
                      <div className="bg-indigo-500 flex-[2] flex items-center justify-center">
                        <span className="text-[10px] font-bold text-white">8</span>
                      </div>
                      <div className="bg-violet-500 flex-[2] flex items-center justify-center">
                        <span className="text-[10px] font-bold text-white">6</span>
                      </div>
                      <div className="bg-emerald-500 rounded-r-lg flex-1 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-white">4</span>
                      </div>
                    </div>
                    <div className="flex gap-1 mt-1">
                      <span className="text-[9px] text-slate-500 flex-[3]">Prospect</span>
                      <span className="text-[9px] text-slate-500 flex-[2]">RDV</span>
                      <span className="text-[9px] text-slate-500 flex-[2]">Devis</span>
                      <span className="text-[9px] text-slate-500 flex-1">Signé</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating card - CEE */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-2xl p-4 border border-slate-100 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
                    <BadgeEuro className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Potentiel CEE détecté</p>
                    <p className="text-xl font-black text-slate-900">1.2 M€</p>
                  </div>
                </div>
              </div>
              
              {/* Floating card - Gamification */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-2xl p-4 border border-slate-100 animate-float-delayed">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Top performer</p>
                    <p className="text-lg font-bold text-slate-900">Marie D.</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-xs font-semibold text-amber-600">1 850 pts</span>
                      <span className="text-xs">🏆</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Database Power Section - HIGH IMPACT */}
      <section className="py-16 lg:py-20 px-6 lg:px-12 bg-gradient-to-br from-slate-900 via-slate-800 to-[#1E3A8A] relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500 rounded-full blur-[120px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur border border-white/20 rounded-full mb-6">
              <Database className="w-5 h-5 text-emerald-400" />
              <span className="text-sm font-semibold text-white">Données Officielles Exclusives</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
              Accès direct aux bases nationales
            </h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              CoproEnergy agrège et enrichit les données officielles du registre national des copropriétés 
              et des diagnostics de performance énergétique.
            </p>
          </div>

          {/* Big numbers */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors group">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <p className="text-4xl lg:text-5xl font-black text-white mb-1">200K+</p>
              <p className="text-blue-200 font-medium">Copropriétés</p>
              <p className="text-xs text-blue-300/70 mt-1">Registre National (RNIC)</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors group">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <p className="text-4xl lg:text-5xl font-black text-white mb-1">5M+</p>
              <p className="text-emerald-200 font-medium">DPE Analysés</p>
              <p className="text-xs text-emerald-300/70 mt-1">Base ADEME</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors group">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Flame className="w-7 h-7 text-white" />
              </div>
              <p className="text-4xl lg:text-5xl font-black text-white mb-1">67%</p>
              <p className="text-amber-200 font-medium">Classes E, F, G</p>
              <p className="text-xs text-amber-300/70 mt-1">Fort potentiel CEE</p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-colors group">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Target className="w-7 h-7 text-white" />
              </div>
              <p className="text-4xl lg:text-5xl font-black text-white mb-1">100%</p>
              <p className="text-purple-200 font-medium">Matching Auto</p>
              <p className="text-xs text-purple-300/70 mt-1">RNIC ↔ DPE</p>
            </div>
          </div>

          {/* Value proposition boxes */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* RNIC Box */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-8 hover:border-blue-400/50 transition-colors">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Building className="w-8 h-8 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Registre National des Copropriétés</h3>
                  <p className="text-blue-200 text-sm">Source : data.gouv.fr - Mise à jour quotidienne</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span>Nom, adresse et géolocalisation de chaque copropriété</span>
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span>Syndic de gestion (professionnel ou bénévole)</span>
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span>Nombre de lots d'habitation et stationnement</span>
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span>Période de construction du bâtiment</span>
                </li>
              </ul>
            </div>

            {/* DPE Box */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-8 hover:border-emerald-400/50 transition-colors">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-8 h-8 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Diagnostics de Performance Énergétique</h3>
                  <p className="text-emerald-200 text-sm">Source : ADEME - Observatoire DPE</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span>Classe énergétique (A à G) et émissions GES</span>
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span>Type de chauffage et énergie principale</span>
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span>Consommation énergétique en kWh/m²/an</span>
                </li>
                <li className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span>Surface habitable et année de construction</span>
                </li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-white hover:bg-slate-100 text-[#1E3A8A] rounded-full px-8 h-14 text-lg font-semibold shadow-2xl shadow-black/20 hover:scale-105 transition-transform"
              >
                <Database className="w-5 h-5 mr-2" />
                Explorer la base de données
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-blue-200/70 text-sm mt-4">Accès immédiat après inscription • 7 jours gratuits</p>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-16 bg-[#1E3A8A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Vous vous reconnaissez ?
            </h2>
            <p className="text-blue-200">Nous avons la solution.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {painPoints.map((item) => (
              <div key={item.problem} className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/10">
                <p className="text-white/80 text-sm mb-4 italic">"{item.problem}"</p>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-white font-medium text-sm">{item.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="resultats" className="py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full mb-4">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-700">Résultats prouvés</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight mb-4">
              Augmentez votre performance commerciale
            </h2>
            <p className="text-lg text-[#64748B]">
              Nos clients constatent des résultats mesurables dès les premières semaines d'utilisation.
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {results.map((item) => (
              <div key={item.label} className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-100 text-center hover:shadow-xl transition-shadow">
                <p className="text-4xl lg:text-5xl font-black text-[#1E3A8A] mb-2">{item.value}</p>
                <p className="text-[#0F172A] font-semibold mb-1">{item.label}</p>
                <p className="text-xs text-[#64748B]">{item.sublabel}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="solutions" className="py-20 lg:py-28 px-6 lg:px-12 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight mb-4">
              Une plateforme complète pour votre 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6]"> développement commercial</span>
            </h2>
            <p className="text-lg text-[#64748B]">
              Tous les outils pour prospecter, qualifier et convertir efficacement 
              sur le marché de la rénovation énergétique en copropriété.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div 
                key={feature.title}
                className="group bg-white rounded-2xl p-8 border border-slate-100 hover:border-blue-200 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 relative"
              >
                <div className="absolute top-4 right-4 px-2 py-1 bg-blue-50 rounded-full">
                  <span className="text-[10px] font-bold text-[#1E3A8A]">{feature.highlight}</span>
                </div>
                
                <div className="w-14 h-14 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl flex items-center justify-center mb-6 group-hover:from-[#1E3A8A] group-hover:to-[#3B82F6] transition-all duration-300">
                  <feature.icon className="w-7 h-7 text-[#1E3A8A] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">{feature.title}</h3>
                <p className="text-[#64748B] leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight mb-4">
              Conçu pour les professionnels du 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6]"> bâtiment résidentiel</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {useCases.map((item) => (
              <div key={item.title} className="flex items-start gap-4 p-6 rounded-2xl bg-slate-50 border border-slate-100">
                <div className="w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] mb-1">{item.title}</h3>
                  <p className="text-sm text-[#64748B]">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-28 px-6 lg:px-12 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight mb-4">
              Ils développent leur business avec CoproEnergy
            </h2>
            <p className="text-lg text-[#64748B]">
              Découvrez comment nos clients optimisent leur prospection commerciale.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.name}
                className="bg-white rounded-2xl p-8 border border-slate-100 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={`star-${i}`} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-[#0F172A] mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full border-2 border-blue-100"
                  />
                  <div>
                    <p className="font-semibold text-[#0F172A]">{testimonial.name}</p>
                    <p className="text-sm text-[#64748B]">{testimonial.role}</p>
                    <p className="text-xs text-[#1E3A8A] font-medium">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="tarifs" className="py-20 lg:py-28 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight mb-4">
              Investissez dans votre croissance
            </h2>
            <p className="text-lg text-[#64748B]">
              Un ROI visible dès le premier mois. Essai gratuit 7 jours, sans engagement.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Monthly - Sans engagement */}
            <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 hover:border-slate-200 transition-colors">
              <div className="mb-6">
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Sans engagement</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-black text-[#0F172A]">129€</span>
                  <span className="text-[#64748B]">HT / licence / mois</span>
                </div>
                <p className="text-sm text-[#64748B] mt-2">Flexibilité totale • Résiliable à tout moment</p>
              </div>
              
              <div className="space-y-3 mb-8">
                {[
                  "Accès plateforme complète",
                  "Base 200K+ copropriétés",
                  "Matching RNIC + DPE illimité",
                  "Calcul BAR-TH-179",
                  "Automatisation emails",
                  "Pipeline commercial & Kanban",
                  "Cartographie interactive",
                  "Gamification équipes"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-[#0F172A]">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link to="/register">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full rounded-full h-12 text-base border-2"
                >
                  Démarrer l'essai gratuit
                </Button>
              </Link>
            </div>
            
            {/* Annual - Avec engagement */}
            <div className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] rounded-3xl p-8 relative overflow-hidden shadow-2xl shadow-blue-900/30">
              <div className="absolute top-4 right-4 bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full">
                ÉCONOMISEZ 23%
              </div>
              
              <div className="mb-6">
                <p className="text-sm font-semibold text-blue-200 uppercase tracking-wide mb-2">Avec engagement annuel</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-black text-white">99€</span>
                  <span className="text-blue-200">HT / licence / mois</span>
                </div>
                <p className="text-sm text-blue-200 mt-2">Facturé 1 188€ HT/an par licence</p>
              </div>
              
              <div className="space-y-3 mb-8">
                {[
                  "Tout du plan Sans engagement",
                  "Support prioritaire",
                  "Onboarding personnalisé",
                  "Accès anticipé nouveautés",
                  "Rapports avancés",
                  "Account manager dédié"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-300 flex-shrink-0" />
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="w-full bg-white text-[#1E3A8A] hover:bg-blue-50 rounded-full h-12 text-base font-semibold"
                  data-testid="pricing-cta-btn"
                >
                  Démarrer maintenant
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
          
          <div className="text-center mt-10">
            <p className="text-sm text-[#64748B]">
              Besoin d'un déploiement sur-mesure ? <a href="#" className="text-[#1E3A8A] font-semibold hover:underline">Contactez notre équipe commerciale</a>
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 lg:py-28 px-6 lg:px-12 bg-gradient-to-br from-[#1E3A8A] via-[#1E40AF] to-[#1E3A8A] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOGM5Ljk0MSAwIDE4LTguMDU5IDE4LTE4cy04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAzIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-blue-200" />
            <span className="text-sm font-medium text-blue-100">Essai gratuit • 7 jours • Sans carte bancaire</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
            Prêt à accélérer votre développement commercial ?
          </h2>
          <p className="text-lg text-blue-100 mb-10 max-w-2xl mx-auto">
            Rejoignez les entreprises qui utilisent CoproEnergy pour identifier et convertir 
            les meilleures opportunités de rénovation énergétique.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-white text-[#1E3A8A] hover:bg-blue-50 rounded-full px-8 h-14 text-lg font-semibold shadow-xl"
                data-testid="final-cta-btn"
              >
                Créer mon compte gratuit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white/50 text-white hover:bg-white/10 rounded-full px-8 h-14 text-lg"
              >
                <Play className="w-5 h-5 mr-2" />
                Voir la démo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 lg:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-6">
              <MessageSquare className="w-4 h-4 text-[#1E3A8A]" />
              <span className="text-sm font-semibold text-[#1E3A8A]">Contactez-nous</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mb-4">
              Une question ? Un projet ?
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              Notre équipe est à votre disposition pour répondre à toutes vos questions 
              et vous accompagner dans votre développement commercial.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8 lg:p-12">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {/* Contact Info */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-[#0F172A]">Nos coordonnées</h3>
                
                <div className="space-y-4">
                  <a 
                    href="mailto:contact@copro-energy.com" 
                    className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-blue-50 transition-colors group"
                  >
                    <div className="w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Email</p>
                      <p className="text-lg font-semibold text-[#0F172A] group-hover:text-[#1E3A8A] transition-colors">
                        contact@copro-energy.com
                      </p>
                    </div>
                  </a>

                  <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-[#1E3A8A] mt-0.5" />
                      <div>
                        <p className="font-semibold text-[#0F172A]">Temps de réponse</p>
                        <p className="text-sm text-[#64748B]">Nous vous répondons sous 24h ouvrées</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <p className="text-sm text-[#64748B] mb-3">Vous préférez une démonstration ?</p>
                  <Link to="/register">
                    <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white rounded-full px-6">
                      <Play className="w-4 h-4 mr-2" />
                      Demander une démo gratuite
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Quick Contact Form - Simple mailto */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-[#0F172A]">Envoyez-nous un message</h3>
                
                <p className="text-[#64748B]">
                  Cliquez sur le bouton ci-dessous pour nous envoyer un email directement 
                  depuis votre messagerie.
                </p>

                <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                  <div className="flex items-center gap-3 text-[#64748B]">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Demande de démonstration</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#64748B]">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Questions sur les tarifs</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#64748B]">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Partenariats et intégrations</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#64748B]">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Support technique</span>
                  </div>
                </div>

                <a 
                  href="mailto:contact@copro-energy.com?subject=Demande%20d'information%20CoproEnergy"
                  className="block"
                >
                  <Button 
                    size="lg" 
                    className="w-full bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] hover:from-[#1E40AF] hover:to-[#2563EB] text-white rounded-xl h-14 text-lg shadow-lg shadow-blue-900/20"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Envoyer un email
                  </Button>
                </a>

                <p className="text-xs text-center text-[#94A3B8]">
                  Votre application email s'ouvrira automatiquement
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white">CoproEnergy</span>
            </div>
            
            <div className="flex items-center gap-8 text-slate-400 text-sm">
              <a href="#solutions" className="hover:text-white transition-colors">Solutions</a>
              <a href="#resultats" className="hover:text-white transition-colors">Résultats</a>
              <a href="#tarifs" className="hover:text-white transition-colors">Tarifs</a>
              <a href="#contact" className="hover:text-white transition-colors">Contact</a>
              <Link to="/login" className="hover:text-white transition-colors">Connexion</Link>
            </div>
            
            <div className="flex items-center gap-4">
              <Shield className="w-5 h-5 text-slate-500" />
              <span className="text-sm text-slate-500">Données sécurisées RGPD</span>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-slate-500 text-sm">
              © {new Date().getFullYear()} CoproEnergy. Tous droits réservés.
            </div>
            <div className="flex items-center gap-6 text-slate-500 text-sm">
              <Link to="/legal/cgv" className="hover:text-white transition-colors">CGV</Link>
              <Link to="/legal/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link>
              <Link to="/legal/confidentialite" className="hover:text-white transition-colors">Confidentialité</Link>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Floating animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 4s ease-in-out infinite;
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
